import React, { useRef, useState } from 'react';
import {
  hapticFeedbackImpactOccurred,
  isHapticFeedbackSupported
} from '@telegram-apps/sdk-react';

import { MessageUI } from '@/api/messaging/types.ts';

interface UseSwipeToReplyParams {
  message: MessageUI;
  isSwipeDisabled: boolean;
  onReply?: (message: MessageUI) => void;
  contentRef?: React.RefObject<HTMLDivElement>;
}

export const useSwipeToReply = ({
  message,
  isSwipeDisabled,
  onReply,
  contentRef
}: UseSwipeToReplyParams) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const hasVibrated = useRef(false);
  const isHorizontalSwipe = useRef(false);

  const triggerVibration = () => {
    if (isHapticFeedbackSupported()) {
      hapticFeedbackImpactOccurred('rigid');
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isSwipeDisabled) return;

    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setIsSwiping(true);
    hasVibrated.current = false;
    isHorizontalSwipe.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = touchStartX.current - currentX;
    const diffY = Math.abs(touchStartY.current - currentY);

    if (!isHorizontalSwipe.current && (Math.abs(diffX) > 5 || diffY > 5)) {
      isHorizontalSwipe.current = Math.abs(diffX) > diffY;
    }

    e.preventDefault();
    e.stopPropagation();

    if (isHorizontalSwipe.current && diffX > 0) {
      if (contentRef?.current) {
        contentRef.current.style.overflowY = 'hidden';
      }

      const clampedDiff = Math.min(diffX, 40);
      setSwipeOffset(clampedDiff);

      // Haptic if real swipe > 40px
      if (diffX >= 40 && !hasVibrated.current) {
        triggerVibration();
        hasVibrated.current = true;
      }
    }
  };

  const handleTouchEnd = () => {
    if (swipeOffset >= 40 && onReply) {
      onReply(message);
    }

    setSwipeOffset(0);
    setIsSwiping(false);

    if (contentRef?.current) {
      contentRef.current.style.overflowY = 'auto';
    }
  };

  return {
    swipeOffset,
    isSwiping,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  };
};
